import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";
import deepResearchStore from "Stores/deepResearchData";
import { toasterState } from "Components/common/toaster";
import RerunResearchPopup from "Pages/Dashboard/DeepResearch/components/RerunResearchPopup";
import ResearchingPlaceholder from "Pages/Dashboard/DeepResearch/components/ResearchingPlaceholder";
import SellerProfileSetup from "Pages/Dashboard/DeepResearch/components/SellerProfileSetup";
import SellerIntelligenceCard from "Pages/Dashboard/DeepResearch/components/SellerIntelligenceCard";
import { PdfDownloadButton, PdfPrintHeader } from "Components/common/PdfExport";

/**
 * Account Deep Research surface — STRATEGIC RESEARCH LAYER for senior sales
 * leaders. NOT a rep tactical card. The leader reads this, forms strategy,
 * then assigns tasks to reps for execution. Messaging/personalization lives
 * downstream (contact DR + composer).
 *
 * 9-field schema (8 static + 1 dynamic signals[]). See AccountResearchService
 * .buildAccountInsightSchema() for the contract.
 */

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};
const fmtAge = (d) => {
  if (!d) return "";
  const ms = Date.now() - new Date(d).getTime();
  const day = 86_400_000;
  if (ms < day) return "today";
  if (ms < 2 * day) return "yesterday";
  const days = Math.floor(ms / day);
  if (days < 30) return `${days}d ago`;
  return fmtDate(d);
};
const labelForProfile = (p) =>
  p?.name || p?.company_name || p?.product_name || "Unnamed profile";

const hostOf = (u) => {
  if (!u) return "";
  try { return new URL(u).hostname.replace(/^www\./, ""); }
  catch (e) { return ""; }
};

// ── Shared primitives ───────────────────────────────────────────────────

const InsightBlock = ({ icon, title, count, headerExtra, children }) => (
  <div className="acov-aint__block">
    <div className="acov-aint__block-header">
      <span className="acov-aint__block-icon">{icon}</span>
      <h5 className="acov-aint__block-title">{title}</h5>
      {count != null && <span className="acov-aint__block-count">{count}</span>}
      {headerExtra}
    </div>
    {children}
  </div>
);

// Emphasizes proper nouns + skim-worthy data points (people, companies,
// products, $-amounts, %-figures, years) with QUIET styling: slightly
// bolder weight + slightly darker color. No underline, no background,
// no purple — calm by default, eye still catches the important bits.
const HighlightNouns = ({ text }) => {
  if (!text || typeof text !== "string") return text || null;
  // Match: $amounts | percentages | 4-digit years | Capitalized phrases (1+ words)
  const re = /(\$[\d,.]+(?:\s*[KMB])?|\d+(?:\.\d+)?%|\b(?:19|20)\d{2}\b|\b[A-Z][a-zA-Z0-9'&]+(?:\s+[A-Z][a-zA-Z0-9'&]+){0,4})\b/g;
  // Avoid emphasizing first-word-of-sentence capitalization (The/As/With/etc.)
  const STOP = new Set(["The","As","With","In","On","For","At","To","From","By","Of","And","Or","But","If","When","While","After","Before","Their","This","That","These","Those","An","A","Is","Are","Was","Were","Has","Have","Had","Will","Would","Could","Should","May","Might","Must"]);
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    const words = tok.split(/\s+/);
    if (words.length === 1 && STOP.has(words[0])) {
      parts.push(tok);
    } else {
      parts.push(<span key={parts.length} className="acov-aint__noun">{tok}</span>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const ConfidenceBadge = ({ level }) => {
  if (!level) return null;
  const norm = String(level).toLowerCase();
  return (
    <span className={`acov-aint__conf acov-aint__conf--${norm}`}>{norm} confidence</span>
  );
};

// ── DECISION BANNER (exec triage strip — first thing leader reads) ─────

const AlertBanner = ({ status }) => {
  if (!status || status === "go") return null;
  const map = {
    direct_competitor: { tone: "red",    icon: "⛔", text: "DIRECT COMPETITOR — this account sells a product competing with yours" },
    adjacent_competitor: { tone: "yellow", icon: "⚠️", text: "ADJACENT COMPETITOR — overlapping capabilities" },
    partner_potential: { tone: "blue",  icon: "🤝", text: "PARTNER POTENTIAL — consider integration/distribution play, not standard sale" }
  };
  const c = map[status];
  if (!c) return null;
  return (
    <div className={`acov-aint__alert acov-aint__alert--${c.tone}`}>
      <span className="acov-aint__alert-icon">{c.icon}</span>
      <span className="acov-aint__alert-text">{c.text}</span>
    </div>
  );
};

const DecisionBanner = ({ decision }) => {
  if (!decision) return null;
  const pursue = decision.should_pursue !== false;
  return (
    <div className={`acov-aint__decision acov-aint__decision--${pursue ? "go" : "hold"}`}>
      <div className="acov-aint__decision-head">
        <span className={`acov-aint__decision-verdict acov-aint__decision-verdict--${pursue ? "go" : "hold"}`}>
          {pursue ? "PURSUE" : "HOLD"}
        </span>
        {decision.tier_recommendation && (
          <span className="acov-aint__decision-pill acov-aint__decision-pill--tier">
            {decision.tier_recommendation}
          </span>
        )}
        {decision.sales_cycle_estimate && (
          <span className="acov-aint__decision-pill">Cycle: {decision.sales_cycle_estimate}</span>
        )}
        {decision.budget_envelope && (
          <span className="acov-aint__decision-pill">Budget: {decision.budget_envelope}</span>
        )}
        <ConfidenceBadge level={decision.confidence} />
      </div>
      {decision.one_sentence && (
        <p className="acov-aint__decision-line"><HighlightNouns text={decision.one_sentence} /></p>
      )}
      {Array.isArray(decision.three_bullets) && decision.three_bullets.length > 0 && (
        <ul className="acov-aint__decision-bullets">
          {decision.three_bullets.map((b, i) => <li key={i}><HighlightNouns text={b} /></li>)}
        </ul>
      )}
      {decision.budget_reasoning && (
        <p className="acov-aint__decision-sub">
          <span>Budget read:</span> {decision.budget_reasoning}
        </p>
      )}
    </div>
  );
};

// ── ICP score (compact, sits next to decision) ─────────────────────────

const ICPBadge = ({ icp }) => {
  if (!icp || icp.score == null) return null;
  const score = Number(icp.score) || 0;
  const tone = score >= 75 ? "high" : score >= 50 ? "med" : "low";
  return (
    <InsightBlock
      icon="🎯"
      title="ICP Match"
      headerExtra={<ConfidenceBadge level={icp.confidence} />}
    >
      <div className={`acov-aint__icp acov-aint__icp--${tone}`}>
        <span className="acov-aint__icp-score">{score}</span>
        <span className="acov-aint__icp-max">/ 100</span>
      </div>
      {icp.reasoning && <p className="acov-aint__icp-reason">{icp.reasoning}</p>}
    </InsightBlock>
  );
};

// ── COMPETITIVE POSITION — vendor stack + account's own competitors ────

const CompetitivePosition = ({ cp }) => {
  if (!cp) return null;
  const competitors = Array.isArray(cp.account_competitors) ? cp.account_competitors : [];
  const stack = Array.isArray(cp.competitor_in_stack) ? cp.competitor_in_stack : [];
  const complementary = Array.isArray(cp.complementary_tools_in_stack) ? cp.complementary_tools_in_stack : [];

  return (
    <InsightBlock
      icon="⚔️"
      title="Competitive Position"
      headerExtra={<ConfidenceBadge level={cp.confidence} />}
    >
      <div className="acov-aint__cp-row">
        {cp.deal_type && (
          <span className={`acov-aint__cp-deal acov-aint__cp-deal--${String(cp.deal_type).toLowerCase().replace(/\s+/g, "-")}`}>
            {cp.deal_type}
          </span>
        )}
        {cp.rip_and_replace_risk && (
          <span className="acov-aint__cp-pill">
            Switching risk: <strong>{cp.rip_and_replace_risk}</strong>
          </span>
        )}
      </div>

      {cp.strategic_implication && (
        <p className="acov-aint__cp-impl">{cp.strategic_implication}</p>
      )}

      {stack.length > 0 && (
        <div className="acov-aint__cp-section">
          <p className="acov-aint__cp-label">Our competitors already in their stack</p>
          <div className="acov-aint__pills">
            {stack.map((t, i) => <span key={i} className="acov-aint__pill acov-aint__pill--warn">{t}</span>)}
          </div>
        </div>
      )}

      {complementary.length > 0 && (
        <div className="acov-aint__cp-section">
          <p className="acov-aint__cp-label">Complementary tools (readiness signals)</p>
          <div className="acov-aint__pills">
            {complementary.map((t, i) => <span key={i} className="acov-aint__pill">{t}</span>)}
          </div>
        </div>
      )}

      {competitors.length > 0 && (
        <div className="acov-aint__cp-section">
          <p className="acov-aint__cp-label">Their competitors (top {competitors.length})</p>
          <ul className="acov-aint__acomps">
            {competitors.map((c, i) => (
              <li key={i} className="acov-aint__acomp">
                <div className="acov-aint__acomp-head">
                  <strong className="acov-aint__acomp-name">{c.name}</strong>
                  {c.segment_of_overlap && (
                    <span className="acov-aint__acomp-seg">{c.segment_of_overlap}</span>
                  )}
                </div>
                {c.why_top_pick && <p className="acov-aint__acomp-why">{c.why_top_pick}</p>}
                {c.recent_move && (
                  <p className="acov-aint__acomp-move"><span>Recent move:</span> {c.recent_move}</p>
                )}
                {c.wedge_for_outreach && (
                  <p className="acov-aint__acomp-wedge"><span>Wedge:</span> {c.wedge_for_outreach}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </InsightBlock>
  );
};

// ── ACCOUNT STRATEGY (thesis + wedges + supporting signals) ────────────

const SignalCitationPills = ({ labels, onClick }) => {
  if (!Array.isArray(labels) || labels.length === 0) return null;
  return (
    <div className="acov-aint__cites">
      <span className="acov-aint__cites-label">Backed by:</span>
      {labels.map((l, i) => (
        <button
          key={i}
          type="button"
          className="acov-aint__cite-pill"
          onClick={() => onClick && onClick(l)}
          title="Jump to supporting signal"
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const AccountStrategy = ({ strategy, onCiteClick }) => {
  if (!strategy) return null;
  return (
    <InsightBlock
      icon="🧭"
      title="Account Strategy"
      headerExtra={<ConfidenceBadge level={strategy.confidence} />}
    >
      {strategy.strategic_thesis && (
        <div className="acov-aint__strat-thesis">
          <span className="acov-aint__strat-label">Strategic thesis</span>
          <p><HighlightNouns text={strategy.strategic_thesis} /></p>
        </div>
      )}
      <div className="acov-aint__strat-wedges">
        {strategy.primary_wedge && (
          <div className="acov-aint__strat-wedge acov-aint__strat-wedge--primary">
            <span className="acov-aint__strat-wedge-label">Primary wedge</span>
            <p>{strategy.primary_wedge}</p>
          </div>
        )}
        {strategy.secondary_wedge && (
          <div className="acov-aint__strat-wedge">
            <span className="acov-aint__strat-wedge-label">Secondary wedge</span>
            <p>{strategy.secondary_wedge}</p>
          </div>
        )}
      </div>
      {strategy.timing_rationale && (
        <p className="acov-aint__strat-line">
          <span>Timing:</span> {strategy.timing_rationale}
        </p>
      )}
      {/* why_we_can_win — new shape: array of {point, detail}. Backward-compat:
          old cached briefs may have it as a string; render those as a single line. */}
      {Array.isArray(strategy.why_we_can_win) && strategy.why_we_can_win.length > 0 ? (
        <div className="acov-aint__strat-wins">
          <span className="acov-aint__strat-wins-label">Why we can win</span>
          <div className="acov-aint__strat-wins-list">
            {strategy.why_we_can_win.filter(w => w && (w.point || w.detail)).map((w, i) => (
              <div key={i} className="acov-aint__strat-win">
                {w.point && <span className="acov-aint__strat-win-badge">{w.point}</span>}
                {w.detail && (
                  <p className="acov-aint__strat-win-detail">
                    <HighlightNouns text={String(w.detail).replace(/\s*\(not in source\)/gi, "").trim()} />
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (typeof strategy.why_we_can_win === "string" && strategy.why_we_can_win.trim() && (
        <p className="acov-aint__strat-line">
          <span>Why we can win:</span>{" "}
          <HighlightNouns text={strategy.why_we_can_win.replace(/\s*\(not in source\)/gi, "").trim()} />
        </p>
      ))}
      <SignalCitationPills
        labels={strategy.supporting_signal_labels}
        onClick={onCiteClick}
      />
    </InsightBlock>
  );
};

// ── ENGAGEMENT PLAN — priority roles + decision dynamics + org tree ────

const EngagementPlan = ({ plan, onFindContacts }) => {
  if (!plan) return null;
  const roles = Array.isArray(plan.priority_roles) ? plan.priority_roles : [];
  const ordered = [...roles].sort((a, b) =>
    (Number(a.engagement_order) || 99) - (Number(b.engagement_order) || 99));
  const dd = plan.decision_dynamics || {};

  return (
    <InsightBlock
      icon="🎯"
      title="Engagement Plan"
      headerExtra={<ConfidenceBadge level={plan.confidence} />}
    >
      {ordered.length > 0 && (
        <ol className="acov-aint__roles">
          {ordered.map((r, i) => (
            <li key={i} className="acov-aint__role">
              <div className="acov-aint__role-head">
                <span className="acov-aint__role-order">{r.engagement_order || i + 1}</span>
                <strong className="acov-aint__role-title">{r.title}</strong>
                {r.name && <span className="acov-aint__role-name">— {r.name}</span>}
                {r.seniority && <span className="acov-aint__role-pill">{r.seniority}</span>}
                {r.meddic_role && <span className="acov-aint__role-pill acov-aint__role-pill--meddic">{r.meddic_role}</span>}
              </div>
              {r.strategic_value && (
                <p className="acov-aint__role-value"><HighlightNouns text={r.strategic_value} /></p>
              )}
              {r.reports_to && (
                <p className="acov-aint__role-org">Reports to: <strong>{r.reports_to}</strong></p>
              )}
              {Array.isArray(r.direct_reports) && r.direct_reports.length > 0 && (
                <p className="acov-aint__role-org">
                  Direct reports: {r.direct_reports.join(", ")}
                </p>
              )}
              {r.warm_path_hint && (
                <p className="acov-aint__role-warm">💡 {r.warm_path_hint}</p>
              )}
              <button
                type="button"
                className="acov-aint__role-cta"
                onClick={() => onFindContacts && onFindContacts(r)}
                disabled={!r.seniority}
                title={r.seniority
                  ? `Open ListBuild filtered to ${r.seniority} at this account`
                  : "Seniority not set"}
              >
                🔍 Find these contacts
              </button>
            </li>
          ))}
        </ol>
      )}

      {plan.org_tree_summary && (
        <div className="acov-aint__org">
          <span className="acov-aint__org-label">Org context</span>
          <p>{plan.org_tree_summary}</p>
        </div>
      )}

      {(dd.decision_style || dd.fast_path || dd.slow_path
        || (dd.typical_signoff_chain && dd.typical_signoff_chain.length > 0)
        || (dd.typical_objections && dd.typical_objections.length > 0)) && (
        <div className="acov-aint__dd">
          <p className="acov-aint__dd-title">Decision dynamics</p>
          <div className="acov-aint__dd-row">
            {dd.decision_style && (
              <span className="acov-aint__dd-pill">Style: <strong>{dd.decision_style}</strong></span>
            )}
            {dd.velocity_estimate && (
              <span className="acov-aint__dd-pill">Velocity: <strong>{dd.velocity_estimate}</strong></span>
            )}
          </div>

          {/* Fast path = the WIN path — hero green callout so it draws the eye */}
          {dd.fast_path && (
            <div className="acov-aint__dd-callout acov-aint__dd-callout--fast">
              <span className="acov-aint__dd-callout-badge acov-aint__dd-callout-badge--fast">⚡ Fast path</span>
              <p className="acov-aint__dd-callout-text"><HighlightNouns text={dd.fast_path} /></p>
            </div>
          )}
          {/* Slow path = caution — softer yellow callout */}
          {dd.slow_path && (
            <div className="acov-aint__dd-callout acov-aint__dd-callout--slow">
              <span className="acov-aint__dd-callout-badge acov-aint__dd-callout-badge--slow">🐢 Slow path</span>
              <p className="acov-aint__dd-callout-text"><HighlightNouns text={dd.slow_path} /></p>
            </div>
          )}

          {/* Sign-off chain — visual flow with arrows */}
          {Array.isArray(dd.typical_signoff_chain) && dd.typical_signoff_chain.length > 0 && (
            <div className="acov-aint__dd-section">
              <span className="acov-aint__dd-section-label">Sign-off chain</span>
              <div className="acov-aint__dd-chain">
                {dd.typical_signoff_chain.map((role, i) => (
                  <React.Fragment key={i}>
                    <span className="acov-aint__dd-chain-step">{role}</span>
                    {i < dd.typical_signoff_chain.length - 1 && (
                      <span className="acov-aint__dd-chain-arrow">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Objections — bulleted list, one per line, with red dot marker */}
          {Array.isArray(dd.typical_objections) && dd.typical_objections.length > 0 && (
            <div className="acov-aint__dd-section">
              <span className="acov-aint__dd-section-label">Typical objections</span>
              <ul className="acov-aint__dd-objs">
                {dd.typical_objections.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {plan.multi_thread_recommendation && (
        <p className="acov-aint__strat-line">
          <span>Multi-thread plan:</span> {plan.multi_thread_recommendation}
        </p>
      )}

      {Array.isArray(plan.do_not_lead_with) && plan.do_not_lead_with.length > 0 && (
        <p className="acov-aint__dnl">
          <span>Do NOT lead with:</span> {plan.do_not_lead_with.join(" · ")}
        </p>
      )}
    </InsightBlock>
  );
};

// ── SIGNALS — grouped by origin, rep_focus first ───────────────────────

const ORIGIN_META = {
  rep_focus:         { label: "Your ask",    cls: "rep" },
  seller_watch_list: { label: "Watching",    cls: "watch" },
  default_category:  { label: "Suggested",   cls: "default" }
};

// section_kind → colour-coded category chip. Keyed by the normalised kind
// (lowercased, spaces → underscores) so "buying signal" and "buying_signal"
// both resolve.
// NOTE: icons must exist in the pinned "Material Icons Outlined v8" font loaded
// in styles/base/_materialIcons.scss. Newer glyphs (badge, campaign, payments,
// rocket_launch, bolt) were added after v8 and render BLANK — use original-era
// ligatures only. (Root fix would be bumping that shared font version.)
const SIGNAL_KIND_META = {
  buying_signal:      { label: "Buying signal",      cls: "buy",   icon: "trending_up" },
  expansion:          { label: "Expansion",          cls: "buy",   icon: "trending_up" },
  leadership_change:  { label: "Leadership change",  cls: "lead",  icon: "supervisor_account" },
  company_voice:      { label: "Company voice",      cls: "voice", icon: "record_voice_over" },
  competitor_move:    { label: "Competitor move",    cls: "neutral", icon: "swap_horiz" },
  vendor_displacement:{ label: "Vendor displacement",cls: "neutral", icon: "swap_horiz" },
  industry_trend:     { label: "Industry trend",     cls: "buy",   icon: "show_chart" },
  financial:          { label: "Financial",          cls: "fin",   icon: "attach_money" },
  funding:            { label: "Funding",            cls: "fin",   icon: "attach_money" },
  funding_event:      { label: "Funding",            cls: "fin",   icon: "attach_money" },
  product_launch:     { label: "Product launch",     cls: "prod",  icon: "launch" },
  product:            { label: "Product",            cls: "prod",  icon: "launch" },
  hiring:             { label: "Hiring",             cls: "hire",  icon: "work" }
};
const signalKindMeta = (k) => {
  if (!k) return null;
  const key = String(k).toLowerCase().trim().replace(/\s+/g, "_");
  return SIGNAL_KIND_META[key]
    || { label: String(k).replace(/_/g, " "), cls: "neutral", icon: "flash_on" };
};

// Humanise raw SERP/source category codes ("COMPANY_PRESS_RELEASES" → "Press release").
const SOURCE_LABELS = {
  COMPANY_PRESS_RELEASES: "Press release",
  COMPANY_RELEVANT_JOB_POSTINGS: "Job posting",
  COMPANY_JOB_POSTINGS: "Job posting",
  COMPANY_LEADERSHIP_CHANGES: "Leadership change",
  COMPANY_CONFERENCE_APPEARANCES: "Conference",
  COMPANY_FINANCIAL_DATA: "Financials",
  COMPANY_NEWS: "News",
  COMPANY_PRODUCT_LAUNCHES: "Product launch",
  COMPANY_COMPETITIVE_SIGNALS: "Competitive"
};
const humanizeSource = (s) => {
  if (!s) return "";
  if (SOURCE_LABELS[s]) return SOURCE_LABELS[s];
  return String(s)
    .replace(/^COMPANY_/, "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const SignalItem = ({ it }) => (
  <li className="acov-aint__sig-item">
    <p className="acov-aint__sig-headline"><HighlightNouns text={it.headline} /></p>
    {it.detail && <p className="acov-aint__sig-detail"><HighlightNouns text={it.detail} /></p>}
    <div className="acov-aint__sig-meta">
      {it.source && (
        <span className="acov-aint__sig-srcPill">{humanizeSource(it.source)}</span>
      )}
      {it.attributed_to && <span className="acov-aint__sig-attr">{it.attributed_to}</span>}
      {it.url && (
        <a className="acov-aint__sig-srcLink" href={it.url} target="_blank" rel="noopener noreferrer">
          View source ↗
        </a>
      )}
    </div>
  </li>
);

const SignalSection = React.forwardRef(({ section }, ref) => {
  const meta = ORIGIN_META[section.origin] || ORIGIN_META.default_category;
  const kind = signalKindMeta(section.section_kind);
  return (
    <div ref={ref} className={`acov-aint__sig acov-aint__sig--${meta.cls}`}>
      <div className="acov-aint__sig-head">
        {kind && (
          <span className={`acov-aint__sig-kind acov-aint__sig-kind--${kind.cls}`}>
            <i className="material-icons-outlined">{kind.icon}</i>
            {kind.label}
          </span>
        )}
        <strong className="acov-aint__sig-label">{section.section_label}</strong>
        {/* Origin tag only when it carries meaning — the default "Suggested"
            was on every card, pure noise. */}
        {section.origin && section.origin !== "default_category" && (
          <span className={`acov-aint__sig-origin acov-aint__sig-origin--${meta.cls}`}>
            {meta.label}
          </span>
        )}
      </div>
      {section.strategic_implication && (
        <div className="acov-aint__sig-why">
          <span className="acov-aint__sig-whyLabel">
            <i className="material-icons-outlined">lightbulb</i>
            Why it matters
          </span>
          <span className="acov-aint__sig-whyText">
            <HighlightNouns text={section.strategic_implication} />
          </span>
        </div>
      )}
      {Array.isArray(section.items) && section.items.length > 0 && (
        <ul className="acov-aint__sig-items">
          {section.items.map((it, i) => <SignalItem key={i} it={it} />)}
        </ul>
      )}
    </div>
  );
});

const SignalsList = ({ signals, signalRefs }) => {
  if (!Array.isArray(signals) || signals.length === 0) return null;
  // Sort: rep_focus → seller_watch_list → default_category, then by seller_relevance desc
  const originOrder = { rep_focus: 0, seller_watch_list: 1, default_category: 2 };
  const sorted = [...signals].sort((a, b) => {
    const oa = originOrder[a.origin] != null ? originOrder[a.origin] : 3;
    const ob = originOrder[b.origin] != null ? originOrder[b.origin] : 3;
    if (oa !== ob) return oa - ob;
    return (Number(b.seller_relevance) || 0) - (Number(a.seller_relevance) || 0);
  });
  return (
    <InsightBlock icon="📡" title="Signals" count={signals.length}>
      <div className="acov-aint__sigs">
        {sorted.map((s, i) => (
          <SignalSection
            key={i}
            section={s}
            ref={(el) => {
              if (signalRefs && s.section_label) {
                signalRefs.current[s.section_label] = el;
              }
            }}
          />
        ))}
      </div>
    </InsightBlock>
  );
};

// ── SUGGESTED CONTACTS widget (Aiark-sourced, seeded by priority_roles) ─

const ContactAvatar = ({ src, initials, alt }) => {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt || ""}
        className="acov-aint__sc-avatar acov-aint__sc-avatar--img"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return <span className="acov-aint__sc-avatar">{initials}</span>;
};

const SuggestedContacts = ({ contacts, onOpenInListBuilder, aiarkFilter }) => {
  if (!Array.isArray(contacts) || contacts.length === 0) return null;
  const titles = Array.isArray(aiarkFilter?.title) ? aiarkFilter.title : [];
  const depts = Array.isArray(aiarkFilter?.department) ? aiarkFilter.department : [];
  const levels = Array.isArray(aiarkFilter?.level) ? aiarkFilter.level : [];
  const rationale = aiarkFilter?.rationale || "";
  const hasFilter = titles.length + depts.length + levels.length > 0;
  return (
    <InsightBlock icon="👥" title="Suggested Contacts" count={contacts.length}>
      <p className="acov-aint__sc-hint">
        Matching the roles recommended above. Click any to open in List Builder
        with this account & seniority pre-filtered.
      </p>
      {hasFilter && (
        <div className="acov-aint__sc-filter">
          {rationale && (
            <p className="acov-aint__sc-filter-rationale">
              <span className="acov-aint__sc-filter-rationale-label">Why these contacts</span>
              {rationale}
            </p>
          )}
          <div className="acov-aint__sc-filter-rows">
            {levels.length > 0 && (
              <div className="acov-aint__sc-filter-row">
                <span className="acov-aint__sc-filter-key">Level</span>
                <div className="acov-aint__sc-filter-pills">
                  {levels.map((l, i) => (
                    <span key={i} className="acov-aint__sc-filter-pill acov-aint__sc-filter-pill--level">{l}</span>
                  ))}
                </div>
              </div>
            )}
            {depts.length > 0 && (
              <div className="acov-aint__sc-filter-row">
                <span className="acov-aint__sc-filter-key">Department</span>
                <div className="acov-aint__sc-filter-pills">
                  {depts.map((d, i) => (
                    <span key={i} className="acov-aint__sc-filter-pill acov-aint__sc-filter-pill--dept">{d}</span>
                  ))}
                </div>
              </div>
            )}
            {titles.length > 0 && (
              <div className="acov-aint__sc-filter-row">
                <span className="acov-aint__sc-filter-key">Title</span>
                <div className="acov-aint__sc-filter-pills">
                  {titles.map((t, i) => (
                    <span key={i} className="acov-aint__sc-filter-pill acov-aint__sc-filter-pill--title">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <ul className="acov-aint__sc-list">
        {contacts.map((c, i) => {
          const fullName = c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "(unnamed)";
          const initials = fullName
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0])
            .join("")
            .toUpperCase() || "?";
          return (
            <li key={i} className="acov-aint__sc-item">
              <ContactAvatar
                src={c.profilePic}
                initials={initials}
                alt={fullName}
              />
              <div className="acov-aint__sc-main">
                <div className="acov-aint__sc-name-row">
                  <strong className="acov-aint__sc-name">{fullName}</strong>
                  {c.seniority && (
                    <span className="acov-aint__sc-pill">{c.seniority}</span>
                  )}
                </div>
                {c.title && <p className="acov-aint__sc-title">{c.title}</p>}
                {c.location && <p className="acov-aint__sc-loc">{c.location}</p>}
              </div>
              <div className="acov-aint__sc-actions">
                {c.linkedIn && (
                  <a
                    href={c.linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="acov-aint__sc-li"
                    title="View on LinkedIn"
                  >
                    in
                  </a>
                )}
                <button
                  type="button"
                  className="acov-aint__sc-cta"
                  onClick={() => onOpenInListBuilder && onOpenInListBuilder(c)}
                  title="Open in List Builder with this account + seniority filtered"
                >
                  Open in ListBuilder →
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </InsightBlock>
  );
};

// ── HIRING SIGNALS from JDs (Jina-extracted, high confidence) ──────────
// Each entry shape (BE): {
//   url, role_title, source_title, team_or_dept, seniority, location,
//   required_tools[], nice_to_have_tools[], competitor_or_replacement_hint,
//   team_size_hint, growth_signal, matchScore, matchedOn[]
// }
// Required_tools = tools the account uses TODAY (JD requires hands-on
// experience). Nice_to_have = tools they're evaluating / value but don't
// necessarily use.

const HiringSignalsFromJDs = ({ jdInsights }) => {
  if (!Array.isArray(jdInsights) || jdInsights.length === 0) return null;

  // Aggregate tools across all JDs for the top-of-section summary chip strip
  const allRequired = new Map(); // tool → count
  const allNiceTo = new Map();
  jdInsights.forEach(j => {
    (j.required_tools || []).forEach(t => {
      const k = String(t).trim();
      if (k) allRequired.set(k, (allRequired.get(k) || 0) + 1);
    });
    (j.nice_to_have_tools || []).forEach(t => {
      const k = String(t).trim();
      if (!k) return;
      if (allRequired.has(k)) return; // already confirmed in stack, skip from "evaluating"
      allNiceTo.set(k, (allNiceTo.get(k) || 0) + 1);
    });
  });
  const requiredChips = Array.from(allRequired.entries())
    .sort((a, b) => b[1] - a[1]);
  const niceChips = Array.from(allNiceTo.entries())
    .sort((a, b) => b[1] - a[1]);

  return (
    <InsightBlock icon="💼" title="Hiring Signals (from JDs)" count={jdInsights.length}>
      <p className="acov-aint__sc-hint">
        Live job openings that match roles you sell to. Tools listed are pulled
        from the actual JD body — confirmed signal of what this account uses today.
      </p>

      {(requiredChips.length > 0 || niceChips.length > 0) && (
        <div className="acov-aint__jd-summary">
          {requiredChips.length > 0 && (
            <div className="acov-aint__jd-summary-row">
              <span className="acov-aint__jd-summary-label">Tools in use</span>
              <div className="acov-aint__jd-chip-row">
                {requiredChips.map(([tool, count]) => (
                  <span key={tool} className="acov-aint__jd-chip acov-aint__jd-chip--required">
                    {tool}{count > 1 ? ` ×${count}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          {niceChips.length > 0 && (
            <div className="acov-aint__jd-summary-row">
              <span className="acov-aint__jd-summary-label">Evaluating / nice-to-have</span>
              <div className="acov-aint__jd-chip-row">
                {niceChips.map(([tool, count]) => (
                  <span key={tool} className="acov-aint__jd-chip acov-aint__jd-chip--nice">
                    {tool}{count > 1 ? ` ×${count}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ul className="acov-aint__jd-list">
        {jdInsights.map((j, i) => {
          const title = j.role_title || j.source_title || "(role)";
          const meta = [j.seniority, j.team_or_dept, j.location]
            .filter(x => x && String(x).trim())
            .join(" · ");
          return (
            <li key={i} className="acov-aint__jd-item">
              <div className="acov-aint__jd-head">
                <strong className="acov-aint__jd-role">{title}</strong>
                {meta && <span className="acov-aint__jd-meta">{meta}</span>}
              </div>
              {Array.isArray(j.required_tools) && j.required_tools.length > 0 && (
                <div className="acov-aint__jd-tools-row">
                  <span className="acov-aint__jd-tools-label">Uses</span>
                  {j.required_tools.map((t, k) => (
                    <span key={k} className="acov-aint__jd-chip acov-aint__jd-chip--required">{t}</span>
                  ))}
                </div>
              )}
              {Array.isArray(j.nice_to_have_tools) && j.nice_to_have_tools.length > 0 && (
                <div className="acov-aint__jd-tools-row">
                  <span className="acov-aint__jd-tools-label">Evaluating</span>
                  {j.nice_to_have_tools.map((t, k) => (
                    <span key={k} className="acov-aint__jd-chip acov-aint__jd-chip--nice">{t}</span>
                  ))}
                </div>
              )}
              {j.growth_signal && (
                <p className="acov-aint__jd-growth">📈 {j.growth_signal}</p>
              )}
              {j.competitor_or_replacement_hint && (
                <p className="acov-aint__jd-hint">⚡ {j.competitor_or_replacement_hint}</p>
              )}
              {j.team_size_hint && (
                <p className="acov-aint__jd-team">👥 {j.team_size_hint}</p>
              )}
              {j.url && (
                <a
                  href={j.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="acov-aint__jd-link"
                >
                  View JD →
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </InsightBlock>
  );
};

// ── RED FLAGS strip ────────────────────────────────────────────────────

const RedFlags = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <InsightBlock icon="🚩" title="Red Flags" count={items.length}>
      <ul className="acov-aint__bullets acov-aint__bullets--red">
        {items.map((f, i) => <li key={i}>{f}</li>)}
      </ul>
    </InsightBlock>
  );
};

// ── RESEARCH LENS footer (transparency about what drove the brief) ─────

// Strings the model sometimes echoes back from prompt placeholders. Treat as empty.
const isPlaceholder = (s) => {
  if (!s || typeof s !== "string") return true;
  const t = s.trim().toLowerCase();
  if (!t) return true;
  return t === "(none)" || t.startsWith("(none") || t === "none"
      || t === "n/a" || t === "(n/a)" || t === "null";
};

const ResearchLensFooter = ({ lens }) => {
  if (!lens) return null;
  const repFocus = isPlaceholder(lens.rep_focus) ? "" : lens.rep_focus;
  const watching = (Array.isArray(lens.seller_watch_list) ? lens.seller_watch_list : [])
    .filter(w => !isPlaceholder(w));
  // Only render when the rep actually customized this run. Empty defaults =
  // no surface noise.
  if (!repFocus && watching.length === 0) return null;
  return (
    <div className="acov-aint__lens">
      <span className="acov-aint__lens-label">🔬 Research lens</span>
      {repFocus && (
        <p className="acov-aint__lens-line">
          <span>Focus for this run:</span> {repFocus}
        </p>
      )}
      {watching.length > 0 && (
        <p className="acov-aint__lens-line">
          <span>Seller's watch list:</span> {watching.join(" · ")}
        </p>
      )}
    </div>
  );
};

// ── Raw research sources (unchanged from prior — for evidence drill-in) ─

// Two-step disclosure: category headers are collapsed by default (just label +
// count). Click to expand and reveal first SRC_PREVIEW URLs. Inside the
// expanded list, a nested "Show N more" reveals the rest.
const SRC_PREVIEW = 10;

const SourceCategory = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, SRC_PREVIEW);
  const remaining = items.length - SRC_PREVIEW;
  return (
    <div className={`acov-aint__src-group ${open ? "acov-aint__src-group--open" : ""}`}>
      <button
        type="button"
        className="acov-aint__src-group-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="acov-aint__src-chev" aria-hidden="true">{open ? "▾" : "▸"}</span>
        <span className="acov-aint__src-group-label">{label}</span>
        <span className="acov-aint__src-group-count">{items.length}</span>
      </button>
      {open && (
        <>
          <ul className="acov-aint__src-list">
            {visible.map((it, i) => {
              const url = it.link || it.url || "";
              const title = it.title || hostOf(url) || url;
              const host = hostOf(url);
              const fav = host
                ? `https://www.google.com/s2/favicons?domain=${host}&sz=32`
                : null;
              return (
                <li key={i} className="acov-aint__src-item" title={url}>
                  {fav && (
                    <img
                      src={fav}
                      alt=""
                      className="acov-aint__src-fav"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                    />
                  )}
                  <a
                    href={url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="acov-aint__src-link"
                  >
                    {title}
                  </a>
                  {host && <span className="acov-aint__src-host">· {host}</span>}
                </li>
              );
            })}
          </ul>
          {remaining > 0 && (
            <button
              type="button"
              className="acov-aint__src-more"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Show less" : `Show ${remaining} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const RawSerpStrip = ({ serpSignals }) => {
  if (!serpSignals || typeof serpSignals !== "object") return null;
  const buckets = Object.entries(serpSignals)
    .filter(([k, v]) => k !== "meta" && Array.isArray(v) && v.length > 0)
    .map(([k, v]) => ({
      label: k.replace(/^COMPANY_/, "").replace(/_/g, " ").toLowerCase(),
      items: v
    }));
  if (buckets.length === 0) return null;
  const total = buckets.reduce((s, b) => s + b.items.length, 0);
  return (
    <InsightBlock icon="📚" title="Research Sources" count={total}>
      <div className="acov-aint__src-groups">
        {buckets.map((b, i) => (
          <SourceCategory key={i} label={b.label} items={b.items} />
        ))}
      </div>
    </InsightBlock>
  );
};

const HistoryStrip = ({ entries, activeId, onSelect }) => {
  if (!Array.isArray(entries) || entries.length < 2) return null;
  return (
    <div className="acov-aint__history">
      <span className="acov-aint__history-label">Research history</span>
      <div className="acov-aint__history-list">
        {entries.map((e) => (
          <button
            key={e.sellerProfileId}
            type="button"
            className={`acov-aint__history-pill ${e.sellerProfileId === activeId ? "acov-aint__history-pill--active" : ""}`}
            onClick={() => onSelect(e.sellerProfileId)}
            title={`Run by ${e.sellerProfileName || e.sellerProfileId} · ${fmtAge(e.generatedAt)}`}
          >
            {e.sellerProfileName || "Untitled"} <span className="acov-aint__history-when">· {fmtAge(e.generatedAt)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────

const AccountIntelligenceSection = observer(({ accountId, accountDomain, accountName }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  // Separately track the synthesized-brief GET (the second wave after raw
  // cache loads). Without this, there's a silent gap where the rep sees
  // "cached signals" + Research Sources and assumes the page is done, then
  // the strategic brief pops in later — confusing UX.
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [rawAccount, setRawAccount] = useState(null);
  const [insights, setInsights] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [activeSellerId, setActiveSellerId] = useState(null);
  const [activeSellerName, setActiveSellerName] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [running, setRunning] = useState(false);
  const [runningTimer, setRunningTimer] = useState(0);
  const [showRerunPopup, setShowRerunPopup] = useState(false);
  const [showSellerSetup, setShowSellerSetup] = useState(false);
  const [streamSessionId, setStreamSessionId] = useState(null);
  const [streamedSerpLinks, setStreamedSerpLinks] = useState({});
  const [pendingRepFocus, setPendingRepFocus] = useState("");
  const [suggestedContacts, setSuggestedContacts] = useState([]);
  const [aiarkFilter, setAiarkFilter] = useState(null);
  const [jdInsights, setJdInsights] = useState([]);
  const [showIntelCard, setShowIntelCard] = useState(null); // {sellerId, sellerName} | null
  const timerRef = useRef(null);
  // Refs to signal section DOMs so citation pills can scroll the user to them.
  const signalRefs = useRef({});

  useEffect(() => { deepResearchStore.fetchSellerProfiles({ force: false }); }, []);

  // Pre-load raw AccountResearch (signals density even pre-synthesis).
  useEffect(() => {
    if (!accountDomain) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    makeApi({
      url: `${URLS.deepResearchAccount}?domain=${encodeURIComponent(accountDomain)}`,
      method: "GET"
    }).then(res => {
      if (cancelled) return;
      setRawAccount(res?.data && res.data.found ? res.data : null);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountDomain]);

  // History strip — every per-seller run for this domain.
  const loadHistory = useCallback(() => {
    if (!accountDomain) return;
    makeApi({
      url: `${URLS.deepResearchAccountHistory}?domain=${encodeURIComponent(accountDomain)}`,
      method: "GET"
    }).then(res => {
      const list = Array.isArray(res?.data?.entries) ? res.data.entries : [];
      setHistoryList(list);
      if (!activeSellerId && list.length > 0) {
        setActiveSellerId(list[0].sellerProfileId);
        setActiveSellerName(list[0].sellerProfileName);
      }
    }).catch(() => {});
  }, [accountDomain, activeSellerId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Restore synthesized brief on refresh / seller switch.
  useEffect(() => {
    if (!accountDomain || !activeSellerId) return undefined;
    let cancelled = false;
    setInsightsLoading(true);
    makeApi({
      url: `${URLS.deepResearchAccountInsights}?domain=${encodeURIComponent(accountDomain)}&sellerProfileId=${encodeURIComponent(activeSellerId)}`,
      method: "GET"
    }).then(res => {
      if (cancelled) return;
      const data = res?.data;
      if (data && data.found && data.insights) {
        setInsights(data.insights);
        setGeneratedAt(data.generatedAt || null);
        if (data.sellerProfileName) setActiveSellerName(data.sellerProfileName);
        setSuggestedContacts(Array.isArray(data.suggested_contacts) ? data.suggested_contacts : []);
        setAiarkFilter(data.aiark_search_filter || null);
        setJdInsights(Array.isArray(data.job_posting_insights) ? data.job_posting_insights : []);
      }
      setInsightsLoading(false);
    }).catch(() => { if (!cancelled) setInsightsLoading(false); });
    return () => { cancelled = true; };
  }, [accountDomain, activeSellerId]);

  useEffect(() => {
    if (!running) return undefined;
    setRunningTimer(0);
    timerRef.current = setInterval(() => setRunningTimer(t => t + 0.1), 100);
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Streaming progress polling while pipeline runs.
  useEffect(() => {
    if (!running || !streamSessionId) return undefined;
    let cancelled = false;
    let cursor = 0;
    const tick = () => {
      if (cancelled) return;
      makeApi({
        url: `${URLS.deepResearchProgress}/${encodeURIComponent(streamSessionId)}?since=${cursor}`,
        method: "GET"
      }).then(res => {
        if (cancelled) return;
        const data = res?.data;
        if (data && Array.isArray(data.events)) {
          for (const evt of data.events) {
            if (!evt) continue;
            if (typeof evt.ts === "number" && evt.ts > cursor) cursor = evt.ts;
            if (evt.type === "serp_bucket_done" && evt.label && Array.isArray(evt.results)) {
              setStreamedSerpLinks(prev => ({ ...prev, [evt.label]: evt.results }));
            }
          }
          if (data.done) return;
        }
        setTimeout(tick, 1200);
      }).catch(() => {
        if (!cancelled) setTimeout(tick, 2500);
      });
    };
    tick();
    return () => { cancelled = true; };
  }, [running, streamSessionId]);

  const startRun = (sellerId, sellerProfileObj, forceFresh, repFocusText) => {
    if (!accountDomain) {
      toasterState.setToastMsg("Set a domain on this account first.", "failure");
      return;
    }
    setRunning(true);
    setActiveSellerId(sellerId);
    setActiveSellerName(labelForProfile(sellerProfileObj));
    const sessionId = `acct-${accountDomain}-${sellerId || "x"}-${Date.now()}`;
    setStreamSessionId(sessionId);
    setStreamedSerpLinks({});

    // Fetch CRM contacts for this account so the BE can mark warm paths and
    // flag seniority gaps in engagement_plan. Best-effort — if the call fails
    // or accountId is unknown, research proceeds without the contacts layer.
    const fetchExistingContacts = accountId
      ? makeApi({
          url: `${URLS.getProspectInsights}/${accountId}/prospect-insights`,
          method: "GET"
        }).then(res => res?.data || null).catch(() => null)
      : Promise.resolve(null);

    fetchExistingContacts.then(existingContacts => makeApi({
      url: URLS.deepResearchAccountProfile,
      method: "POST",
      data: {
        domain: accountDomain,
        seller_profile: sellerProfileObj || {},
        seller_profile_id: sellerId || "",
        force_fresh: !!forceFresh,
        contactIdForProgress: sessionId,
        rep_focus: repFocusText || "",
        existing_contacts: existingContacts || undefined
      }
    })).then(res => {
      const data = res?.data;
      if (data && !data.error) {
        setInsights(data.insights || null);
        setGeneratedAt(data.generated_at || new Date().toISOString());
        setSuggestedContacts(Array.isArray(data.suggested_contacts) ? data.suggested_contacts : []);
        setAiarkFilter(data.aiark_search_filter || null);
        setJdInsights(Array.isArray(data.job_posting_insights) ? data.job_posting_insights : []);
        makeApi({
          url: `${URLS.deepResearchAccount}?domain=${encodeURIComponent(accountDomain)}`,
          method: "GET"
        }).then(r => setRawAccount(r?.data && r.data.found ? r.data : null)).catch(() => {});
        loadHistory();
      } else if (data?.error) {
        toasterState.setToastMsg(`Deep research failed: ${data.error}`, "failure");
      }
      setRunning(false);
    }).catch(err => {
      toasterState.setToastMsg(`Deep research failed: ${err?.message || "unknown error"}`, "failure");
      setRunning(false);
    });
  };

  const openRunPopup = () => {
    const profiles = deepResearchStore.sellerProfiles || [];
    if (profiles.length === 0) { setShowSellerSetup(true); return; }
    setShowRerunPopup(true);
  };

  const handleHistorySelect = (sellerId) => {
    const profiles = deepResearchStore.sellerProfiles || [];
    const seller = profiles.find(p => p.id === sellerId);
    if (seller) startRun(sellerId, seller, false, "");
  };

  // List Builder filter keys (per salesgear-web/dataSearchV2/state.js):
  //   level, title, companyDomain  ← what list builder actually honors
  //   ('seniority' / 'jobTitle' are silently ignored — same trap we hit on the BE)
  const goFindContacts = (role) => {
    if (!accountDomain || !role) return;
    const filters = {
      companyDomain: [accountDomain],
      companySearchType: "domain"
    };
    if (role.seniority) filters.level = [String(role.seniority).toLowerCase()];
    if (role.title) filters.title = [role.title];
    const payload = JSON.stringify({ filters });
    const sgLb = btoa(unescape(encodeURIComponent(payload)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    history.push(`/list-builder?sgLb=${sgLb}`);
  };

  const goOpenContactInListBuilder = (contact) => {
    if (!accountDomain || !contact) return;
    const filters = {
      companyDomain: [accountDomain],
      companySearchType: "domain"
    };
    if (contact.seniority) filters.level = [String(contact.seniority).toLowerCase()];
    if (contact.title) filters.title = [contact.title];
    const payload = JSON.stringify({ filters });
    const sgLb = btoa(unescape(encodeURIComponent(payload)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    history.push(`/list-builder?sgLb=${sgLb}`);
  };

  const scrollToSignal = (label) => {
    const el = signalRefs.current[label];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("acov-aint__sig--flash");
      setTimeout(() => el.classList.remove("acov-aint__sig--flash"), 1400);
    }
  };

  if (!accountDomain) {
    return (
      <section className="acov-aint">
        <div className="acov-aint__header">
          <div>
            <h4 className="acov-aint__title">✦ Account Deep Research</h4>
            <p className="acov-aint__desc">Set a company domain to enable deep research on this account.</p>
          </div>
        </div>
      </section>
    );
  }

  const hasInsights = insights && Object.keys(insights).length > 0;
  const hasRawSignals = rawAccount && rawAccount.companySerpSignals
      && Object.keys(rawAccount.companySerpSignals).length > 0;

  return (
    <section className="acov-aint">
      <div className="acov-aint__header">
        <div>
          <h4 className="acov-aint__title">✦ Account Deep Research</h4>
          <p className="acov-aint__desc">
            {hasInsights
              ? <>Strategic brief · synthesized for <strong>{activeSellerName || "current seller"}</strong> · {fmtAge(generatedAt)}</>
              : (insightsLoading && activeSellerId
                  ? <>Loading strategic brief for <strong>{activeSellerName || "this seller"}</strong>…</>
                  : (hasRawSignals
                      ? `Cached signals from prior research — refreshed ${fmtAge(rawAccount.researchedAt)}. Run fresh deep research for a strategic brief.`
                      : "Strategic research brief for senior planning. Click to generate."))}
          </p>
        </div>
        <div className="acov-aint__header-actions">
          {activeSellerId && (
            <button
              type="button"
              className="acov-aint__lens-btn"
              onClick={() => setShowIntelCard({ sellerId: activeSellerId, sellerName: activeSellerName })}
              title="View the AI-derived buyer personas, competitors, disqualifiers for this seller"
            >
              🧠 Seller lens
            </button>
          )}
          <button
            className="acov-aint__cta"
            onClick={openRunPopup}
            disabled={running}
            title="Run a fresh deep research"
          >
            {running
              ? <>⟳ Researching… <span className="acov-aint__credits">{runningTimer.toFixed(1)}s</span></>
              : <>✦ {hasInsights ? "Re-run" : "Get Deep Research"} <span className="acov-aint__credits">10 credits</span></>}
          </button>
          {hasInsights && !running && (
            <PdfDownloadButton
              fileName={`${accountName || accountDomain || "Account"} - Deep Research`}
              title="Download this brief as a PDF"
            />
          )}
        </div>
      </div>

      <HistoryStrip
        entries={historyList}
        activeId={activeSellerId}
        onSelect={handleHistorySelect}
      />

      {loading && <div className="acov-aint__loading">Loading account intel…</div>}

      <ResearchingPlaceholder
        visible={running}
        subjectName={accountName || accountDomain}
        sellerName={activeSellerName}
        lineIntervalMs={4500}
        customLines={[
          `🔎 Saving the team three hours of digging.`,
          `📇 Watching what this company is saying out loud — not the About Us page.`,
          `🎯 Spotting signals other tools file under "noise".`,
          `🧠 Asking: is this account in season for your pitch?`,
          `⚡ Building the strategy doc, not the email opener.`,
          `✨ Ready for your next account-planning meeting.`
        ]}
      />

      {running && Object.keys(streamedSerpLinks).length > 0 && (
        <div className="acov-aint__body">
          <RawSerpStrip serpSignals={streamedSerpLinks} />
        </div>
      )}

      {!loading && !running && hasInsights && (
        <div className="acov-aint__body pdfPrintArea">
          <PdfPrintHeader
            title={`${accountName || accountDomain || "Account"} — Deep Research`}
            subtitle={activeSellerName ? `Researched for ${activeSellerName}` : ""}
          />
          <AlertBanner status={insights.decision && insights.decision.alert_status} />
          <DecisionBanner decision={insights.decision} />

          <div className="acov-aint__row">
            <ICPBadge icp={insights.icp_match_score} />
            <CompetitivePosition cp={insights.competitive_position} />
          </div>

          {/* Signals first: the timely "why now" feed is what sellers act on. */}
          <SignalsList signals={insights.signals} signalRefs={signalRefs} />
          <AccountStrategy
            strategy={insights.account_strategy}
            onCiteClick={scrollToSignal}
          />
          <EngagementPlan
            plan={insights.engagement_plan}
            onFindContacts={goFindContacts}
          />
          <SuggestedContacts
            contacts={suggestedContacts}
            onOpenInListBuilder={goOpenContactInListBuilder}
            aiarkFilter={aiarkFilter}
          />
          <HiringSignalsFromJDs jdInsights={jdInsights} />
          <RedFlags items={insights.red_flags} />
          <RawSerpStrip serpSignals={rawAccount?.companySerpSignals} />
          <ResearchLensFooter lens={insights.research_lens} />
        </div>
      )}

      {!loading && !running && !hasInsights && hasRawSignals && (
        <div className="acov-aint__body">
          {/* The brief is still being fetched from /accountInsights — show a
              skeleton above the sources so the rep doesn't think this is the
              final state. Without this they wait 2-5s staring at just the
              Research Sources and assume the page is done. */}
          {insightsLoading && activeSellerId && (
            <div className="acov-aint__skeleton">
              <div className="acov-aint__skeleton-spinner" />
              <div>
                <p className="acov-aint__skeleton-title">Loading strategic brief…</p>
                <p className="acov-aint__skeleton-sub">
                  Pulling the synthesized brief for <strong>{activeSellerName || "this seller"}</strong> from cache.
                </p>
              </div>
            </div>
          )}
          <RawSerpStrip serpSignals={rawAccount.companySerpSignals} />
        </div>
      )}

      {!loading && !hasInsights && !hasRawSignals && !running && (
        <div className="acov-aint__empty">
          <span className="acov-aint__empty-icon">🔬</span>
          <p className="acov-aint__empty-text">
            No deep research yet for {accountName || "this account"}. Click <strong>Get Deep Research</strong> to generate a strategic brief.
          </p>
        </div>
      )}

      {showRerunPopup && (
        <RerunResearchPopup
          profiles={deepResearchStore.sellerProfiles || []}
          initialSelectedId={activeSellerId || deepResearchStore.activeSellerProfileId || ""}
          onClose={() => { setShowRerunPopup(false); setPendingRepFocus(""); }}
          onAddNew={() => { setShowRerunPopup(false); setShowSellerSetup(true); }}
          title={hasInsights ? "Re-run deep research" : "Run deep research"}
          runButtonLabel={hasInsights ? "Re-run research" : "Run research"}
          hint="Pick the seller profile. The AI investigates this account through your lens — your ICP, your competitors, your sales motion."
          repFocusValue={pendingRepFocus}
          onRepFocusChange={setPendingRepFocus}
          showLensPreview={true}
          onRun={(sellerId) => {
            setShowRerunPopup(false);
            const profile = (deepResearchStore.sellerProfiles || []).find(p => p.id === sellerId);
            const focus = pendingRepFocus;
            setPendingRepFocus("");
            startRun(sellerId, profile, true, focus);
          }}
        />
      )}

      {showSellerSetup && (
        <SellerProfileSetup
          onClose={() => setShowSellerSetup(false)}
          onSave={(payload) => {
            deepResearchStore.saveSellerProfile({ ...payload, isActive: true }).then(saved => {
              setShowSellerSetup(false);
              if (saved && saved.id) {
                // Kick off the AI-derive in the background — seller intelligence
                // is what makes every future account research sharp. Fire-and-forget;
                // user can edit/view via the lens button anytime.
                makeApi({
                  url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(saved.id)}/derive`,
                  method: "POST",
                  data: {}
                }).catch(() => {});
                const profile = (deepResearchStore.sellerProfiles || []).find(p => p.id === saved.id) || saved;
                startRun(saved.id, profile, true, "");
              }
            });
          }}
        />
      )}

      {showIntelCard && (
        <SellerIntelligenceCard
          sellerProfileId={showIntelCard.sellerId}
          sellerProfileName={showIntelCard.sellerName}
          onClose={() => setShowIntelCard(null)}
        />
      )}
    </section>
  );
});

export default AccountIntelligenceSection;
